import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Button,
    IconButton,
    Box,
    Typography,
    TextField,
    Autocomplete,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper,
    Tabs,
    Tab,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DropzoneArea from '../DropzoneArea';
import EnumsService from '../../../../Services/enums.service';
import { toast } from 'react-toastify';

const ICountImportModal = ({
    open,
    onClose,
    caseId,
    accountantId,
    token,
    onSuccess,
    previewICountClients,
    importICountClients,
    previewICountInvoices,
    importICountInvoices,
    previewICountInvrec,
    importICountInvrec,
    previewICountRefunds,
    importICountRefunds,
    sortCodeIdOptions = [],
    accountCardOptions: accountCardOptionsProp = [],
    client,
    setAccountCardOptions,
    fetchEnums,
    setLoading,
    t,
    direction = 'rtl',
}) => {
    const [tabValue, setTabValue] = useState(0);

    const [clientsFile, setClientsFile] = useState(null);
    const [clientsPreviewData, setClientsPreviewData] = useState(null);
    const [clientsProcessing, setClientsProcessing] = useState(false);

    const [invoicesFile, setInvoicesFile] = useState(null);
    const [invoicesPreviewData, setInvoicesPreviewData] = useState(null);
    const [invoicesProcessing, setInvoicesProcessing] = useState(false);
    const [defaultSortCodeId, setDefaultSortCodeId] = useState('100');
    const [defaultDebitAccountId, setDefaultDebitAccountId] = useState('4');
    const [invoicesPage, setInvoicesPage] = useState(0);
    const [invoicesRowsPerPage, setInvoicesRowsPerPage] = useState(10);

    const [invrecFile, setInvrecFile] = useState(null);
    const [invrecPreviewData, setInvrecPreviewData] = useState(null);
    const [invrecProcessing, setInvrecProcessing] = useState(false);
    const [invrecPaymentSortCodeId, setInvrecPaymentSortCodeId] = useState('3');
    const [paymentTypeToCardId, setPaymentTypeToCardId] = useState({});
    const [invrecPage, setInvrecPage] = useState(0);
    const [invrecRowsPerPage, setInvrecRowsPerPage] = useState(10);

    const [refundsFile, setRefundsFile] = useState(null);
    const [refundsPreviewData, setRefundsPreviewData] = useState(null);
    const [refundsProcessing, setRefundsProcessing] = useState(false);
    const [refundsPage, setRefundsPage] = useState(0);
    const [refundsRowsPerPage, setRefundsRowsPerPage] = useState(10);

    const [localAccountCardOptions, setLocalAccountCardOptions] = useState([]);
    const effectiveCaseId = client?.caseId ?? caseId;

    const accountCardOptions = localAccountCardOptions.length > 0 ? localAccountCardOptions : accountCardOptionsProp;
    const cardsWithoutAddItem = useMemo(
        () => (accountCardOptions || []).filter((c) => c.value1 !== 'AddItem'),
        [accountCardOptions]
    );
    const debitOptions = useMemo(
        () =>
            cardsWithoutAddItem.map((c) => ({
                ...c,
                cardId: c.cardId ?? c.value1,
                label: `${c.name ?? ''} - ${c.cardId ?? ''}`.trim(),
            })),
        [cardsWithoutAddItem]
    );

    const cardIdToName = useMemo(() => {
        const m = new Map();
        (cardsWithoutAddItem || []).forEach((o) => {
            const id = o.cardId ?? o.value1 ?? o.value;
            if (id == null || id === '') return;
            const strId = String(id).trim();
            const name = String(o.name ?? o.label ?? o.accountCaseId ?? '').trim() || strId;
            m.set(strId, name);
            if (/^\d+$/.test(strId)) m.set(Number(strId), name);
        });
        return m;
    }, [cardsWithoutAddItem]);

    useEffect(() => {
        if (!open || !effectiveCaseId || !token) return;
        let cancelled = false;
        (async () => {
            try {
                const res = await EnumsService.GetAccountCardsByCaseId(effectiveCaseId, token);
                if (cancelled || !res?.ok) return;
                const data = await res.json();
                setLocalAccountCardOptions([{ name: t('AddAccountCard'), value1: 'AddItem' }, ...(data || [])]);
                if (setAccountCardOptions)
                    setAccountCardOptions([{ name: t('AddAccountCard'), value1: 'AddItem' }, ...(data || [])]);
            } catch (e) {
                if (!cancelled) console.error('Fetch account cards failed:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [open, effectiveCaseId, token, t, setAccountCardOptions]);

    const formatAmount = (amount) => {
        if (amount == null) return '';
        return new Intl.NumberFormat('he-IL', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    };

    const formatClientDisplay = useCallback(
        (customerId, creditAccountName, creditAccountId) => {
            if (customerId == null && creditAccountId == null) return '—';
            const id = customerId ?? creditAccountId ?? '';
            const name = creditAccountName || (id ? cardIdToName.get(String(id).trim()) || cardIdToName.get(Number(id)) : null);
            return name ? `${name} (${id})` : (id || '—');
        },
        [cardIdToName]
    );

    const handleClose = () => {
        setTabValue(0);
        setClientsFile(null);
        setClientsPreviewData(null);
        setInvoicesFile(null);
        setInvoicesPreviewData(null);
        setInvoicesPage(0);
        setInvrecFile(null);
        setInvrecPreviewData(null);
        setPaymentTypeToCardId({});
        setInvrecPage(0);
        setRefundsFile(null);
        setRefundsPreviewData(null);
        setRefundsPage(0);
        onClose();
    };

    const runClientsPreview = useCallback(async () => {
        if (!clientsFile || !effectiveCaseId || !accountantId || !token || !previewICountClients) return;
        try {
            setClientsProcessing(true);
            const res = await previewICountClients(effectiveCaseId, accountantId, clientsFile, token);
            setClientsPreviewData({
                entries: res.entries ?? [],
                totalRows: res.totalRows ?? 0,
                validCount: res.validCount ?? 0,
                errorCount: res.errorCount ?? 0,
                errorMessages: res.errorMessages ?? [],
            });
        } catch (err) {
            console.error('iCount clients preview failed:', err);
            setClientsPreviewData({
                entries: [],
                totalRows: 0,
                validCount: 0,
                errorCount: 1,
                errorMessages: [err?.message || 'Preview failed'],
            });
        } finally {
            setClientsProcessing(false);
        }
    }, [clientsFile, effectiveCaseId, accountantId, token, previewICountClients]);

    const handleClientsImport = async () => {
        if (!clientsFile || !effectiveCaseId || !token || !importICountClients) return;
        try {
            setClientsProcessing(true);
            const res = await importICountClients(effectiveCaseId, clientsFile, token);
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            if (errors > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}. ${t('Errors')}: ${errors}`);
            } else {
                toast.success(processed > 0 ? `${t('ImportSuccessful')} – ${processed} ${t('processed')}` : t('ImportSuccessful'));
            }
            if (fetchEnums) await fetchEnums();
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('iCount clients import failed:', err);
            toast.error(t('ImportFailed') || err?.message || 'Import failed');
        } finally {
            setClientsProcessing(false);
        }
    };

    const runInvoicesPreview = useCallback(async () => {
        if (!invoicesFile || !effectiveCaseId || !accountantId || !token || !previewICountInvoices) return;
        try {
            setInvoicesProcessing(true);
            const res = await previewICountInvoices(
                effectiveCaseId,
                accountantId,
                invoicesFile,
                defaultSortCodeId || '100',
                defaultDebitAccountId || '4',
                token
            );
            setInvoicesPreviewData({
                entries: res.entries ?? [],
                totalLines: res.totalLines ?? 0,
                validCount: res.validCount ?? 0,
                errorCount: res.errorCount ?? 0,
                errorMessages: res.errorMessages ?? [],
            });
            setInvoicesPage(0);
        } catch (err) {
            console.error('iCount invoices preview failed:', err);
            setInvoicesPreviewData({
                entries: [],
                totalLines: 0,
                validCount: 0,
                errorCount: 1,
                errorMessages: [err?.message || 'Preview failed'],
            });
        } finally {
            setInvoicesProcessing(false);
        }
    }, [invoicesFile, effectiveCaseId, accountantId, token, previewICountInvoices, defaultSortCodeId, defaultDebitAccountId]);

    const handleInvoicesImport = async () => {
        if (!invoicesFile || !effectiveCaseId || !token || !importICountInvoices) return;
        try {
            setInvoicesProcessing(true);
            const res = await importICountInvoices(
                effectiveCaseId,
                invoicesFile,
                defaultSortCodeId || '100',
                defaultDebitAccountId || '4',
                token
            );
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            const duplicateCount = res?.duplicateCount ?? 0;
            const duplicateMessages = res?.duplicateMessages ?? [];
            if (errors > 0 && (res?.errorMessages ?? []).length > 0) {
                toast.error(`${t('ImportFailed')} – ${t('processed')}: ${processed}, ${t('Errors')}: ${errors}`);
            } else if (errors > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}. ${t('Errors')}: ${errors}`);
            } else {
                let msg = processed > 0 ? `${t('ImportSuccessful')} – ${processed} ${t('processed')}` : t('ImportSuccessful');
                if (duplicateCount > 0) {
                    msg += `. ${duplicateCount} ${t('DuplicatesSkipped') || 'duplicates skipped'}`;
                    duplicateMessages.slice(0, 3).forEach((m) => toast.info(m));
                }
                toast.success(msg);
            }
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('iCount invoices import failed:', err);
            toast.error(t('ImportFailed') || err?.message || 'Import failed');
        } finally {
            setInvoicesProcessing(false);
        }
    };

    const runInvrecPreview = useCallback(async () => {
        if (!invrecFile || !effectiveCaseId || !accountantId || !token || !previewICountInvrec) return;
        try {
            setInvrecProcessing(true);
            const res = await previewICountInvrec(effectiveCaseId, accountantId, invrecFile, token);
            setInvrecPreviewData({
                entries: res.entries ?? [],
                distinctPaymentTypes: res.distinctPaymentTypes ?? [],
                totalRows: res.totalRows ?? 0,
                validCount: res.validCount ?? 0,
                errorCount: res.errorCount ?? 0,
                errorMessages: res.errorMessages ?? [],
            });
            setInvrecPage(0);
            setPaymentTypeToCardId((prev) => {
                const next = { ...prev };
                (res.distinctPaymentTypes ?? []).forEach((pt) => {
                    if (next[pt] == null) next[pt] = '';
                });
                return next;
            });
        } catch (err) {
            console.error('iCount invrec preview failed:', err);
            setInvrecPreviewData({
                entries: [],
                distinctPaymentTypes: [],
                totalRows: 0,
                validCount: 0,
                errorCount: 1,
                errorMessages: [err?.message || 'Preview failed'],
            });
        } finally {
            setInvrecProcessing(false);
        }
    }, [invrecFile, effectiveCaseId, accountantId, token, previewICountInvrec]);

    const handleInvrecImport = async () => {
        if (!invrecFile || !effectiveCaseId || !token || !importICountInvrec) return;
        const distinctTypes = invrecPreviewData?.distinctPaymentTypes ?? [];
        const allMapped = distinctTypes.every((pt) => paymentTypeToCardId[pt]);
        if (!allMapped) {
            toast.warning(t('MapAllPaymentTypes') || 'Please map a debit account for each payment type.');
            return;
        }
        const mapping = {};
        distinctTypes.forEach((pt) => {
            const cardId = paymentTypeToCardId[pt];
            if (cardId) mapping[pt] = cardId;
        });
        try {
            setInvrecProcessing(true);
            const res = await importICountInvrec(
                effectiveCaseId,
                invrecFile,
                defaultSortCodeId || '100',
                defaultDebitAccountId || '4',
                invrecPaymentSortCodeId || '3',
                mapping,
                token
            );
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            const duplicateCount = res?.duplicateCount ?? 0;
            const duplicateMessages = res?.duplicateMessages ?? [];
            if (errors > 0 && (res?.errorMessages ?? []).length > 0) {
                toast.error(`${t('ImportFailed')} – ${t('processed')}: ${processed}, ${t('Errors')}: ${errors}`);
            } else if (errors > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}. ${t('Errors')}: ${errors}`);
            } else {
                let msg = processed > 0 ? `${t('ImportSuccessful')} – ${processed} ${t('processed')}` : t('ImportSuccessful');
                if (duplicateCount > 0) {
                    msg += `. ${duplicateCount} ${t('DuplicatesSkipped') || 'duplicates skipped'}`;
                    duplicateMessages.slice(0, 3).forEach((m) => toast.info(m));
                }
                toast.success(msg);
            }
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('iCount invrec import failed:', err);
            toast.error(t('ImportFailed') || err?.message || 'Import failed');
        } finally {
            setInvrecProcessing(false);
        }
    };

    const runRefundsPreview = useCallback(async () => {
        if (!refundsFile || !effectiveCaseId || !accountantId || !token || !previewICountRefunds) return;
        try {
            setRefundsProcessing(true);
            const res = await previewICountRefunds(
                effectiveCaseId,
                accountantId,
                refundsFile,
                defaultSortCodeId || '100',
                defaultDebitAccountId || '4',
                token
            );
            setRefundsPreviewData({
                entries: res.entries ?? [],
                totalLines: res.totalLines ?? 0,
                validCount: res.validCount ?? 0,
                errorCount: res.errorCount ?? 0,
                errorMessages: res.errorMessages ?? [],
            });
            setRefundsPage(0);
        } catch (err) {
            console.error('iCount refunds preview failed:', err);
            setRefundsPreviewData({
                entries: [],
                totalLines: 0,
                validCount: 0,
                errorCount: 1,
                errorMessages: [err?.message || 'Preview failed'],
            });
        } finally {
            setRefundsProcessing(false);
        }
    }, [refundsFile, effectiveCaseId, accountantId, token, previewICountRefunds, defaultSortCodeId, defaultDebitAccountId]);

    const handleRefundsImport = async () => {
        if (!refundsFile || !effectiveCaseId || !token || !importICountRefunds) return;
        try {
            setRefundsProcessing(true);
            const res = await importICountRefunds(
                effectiveCaseId,
                refundsFile,
                defaultSortCodeId || '100',
                defaultDebitAccountId || '4',
                token
            );
            const processed = res?.processedCount ?? 0;
            const errors = res?.errorCount ?? 0;
            const duplicateCount = res?.duplicateCount ?? 0;
            const duplicateMessages = res?.duplicateMessages ?? [];
            if (errors > 0 && (res?.errorMessages ?? []).length > 0) {
                toast.error(`${t('ImportFailed')} – ${t('processed')}: ${processed}, ${t('Errors')}: ${errors}`);
            } else if (errors > 0) {
                toast.warning(`${t('ImportSuccessful')} – ${processed} ${t('processed')}. ${t('Errors')}: ${errors}`);
            } else {
                let msg = processed > 0 ? `${t('ImportSuccessful')} – ${processed} ${t('processed')}` : t('ImportSuccessful');
                if (duplicateCount > 0) {
                    msg += `. ${duplicateCount} ${t('DuplicatesSkipped') || 'duplicates skipped'}`;
                    duplicateMessages.slice(0, 3).forEach((m) => toast.info(m));
                }
                toast.success(msg);
            }
            onSuccess?.();
            handleClose();
        } catch (err) {
            console.error('iCount refunds import failed:', err);
            toast.error(t('ImportFailed') || err?.message || 'Import failed');
        } finally {
            setRefundsProcessing(false);
        }
    };

    if (!open) return null;

    const invrecEntries = invrecPreviewData?.entries ?? [];
    const distinctPaymentTypes = invrecPreviewData?.distinctPaymentTypes ?? [];
    const allPaymentTypesMapped = distinctPaymentTypes.every((pt) => paymentTypeToCardId[pt]);

    return (
        <div className="download-modal-overlay" onClick={handleClose}>
            <div className="download-modal" onClick={(e) => e.stopPropagation()}>
                <div
                    className={`download-modal-content ${direction}`}
                    style={{ position: 'relative', maxWidth: '1100px' }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <h4 className="report-title">
                            {t('ICountCsvImport') || 'iCount CSV'} – {t('Preview')} & {t('Import')}
                        </h4>
                        <IconButton className="client-back-icon" onClick={handleClose} sx={{ padding: '4px' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                        <Tab label={t('ICountClients') || 'Clients'} />
                        <Tab label={t('ICountInvoices') || 'Invoices'} />
                        <Tab label={t('ICountInvrec') || 'Invrec'} />
                        <Tab label={t('ICountRefunds') || 'Refunds'} />
                    </Tabs>

                    {tabValue === 0 && (
                        <Box>
                            <div>
                                <h5>.csv ({t('ICountClients') || 'Clients'})</h5>
                                <DropzoneArea
                                    key={clientsFile ? clientsFile.name : 'icount-clients-empty'}
                                    onDrop={(files) => {
                                        if (files?.length) setClientsFile(files[0]);
                                        setClientsPreviewData(null);
                                    }}
                                    accept=".csv"
                                />
                                {clientsFile && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {t('FileSelected')}: {clientsFile.name}
                                    </Typography>
                                )}
                            </div>
                            <Box display="flex" gap={1.5} mt={2}>
                                <Button variant="contained" onClick={runClientsPreview} disabled={!clientsFile || clientsProcessing}>
                                    {clientsProcessing && !clientsPreviewData ? t('Processing') : t('Preview')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleClientsImport}
                                    disabled={!clientsPreviewData || clientsProcessing || (clientsPreviewData?.validCount ?? 0) === 0}
                                >
                                    {clientsProcessing && clientsPreviewData ? t('Processing') : t('Import')}
                                </Button>
                            </Box>
                            {clientsPreviewData && (
                                <Box mt={3}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {t('TotalLines') || 'Total lines'}: <strong>{clientsPreviewData.totalRows}</strong>
                                        {' | '}
                                        {t('ValidCount') || 'Valid'}: <strong>{clientsPreviewData.validCount}</strong>
                                        {' | '}
                                        {t('ErrorCount') || 'Errors'}: <strong>{clientsPreviewData.errorCount}</strong>
                                    </Typography>
                                    {clientsPreviewData.errorMessages?.length > 0 && (
                                        <Box mb={2} p={1.5} bgcolor="error.light" borderRadius={1} sx={{ maxHeight: 220, overflow: 'auto' }}>
                                            <Typography variant="subtitle2" color="error.dark" sx={{ mb: 0.5 }}>
                                                {t('Errors') || 'Errors'} ({clientsPreviewData.errorMessages.length})
                                            </Typography>
                                            <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                                {clientsPreviewData.errorMessages.map((msg, i) => (
                                                    <li key={i}>
                                                        <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                            {msg}
                                                        </Typography>
                                                    </li>
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                    {clientsPreviewData.entries?.length > 0 && (
                                        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflowX: 'auto' }}>
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Line')}</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>מספר כרטיס</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>שם חברה</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>מספר עוסק</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Email')}</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Status')}</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {clientsPreviewData.entries.map((row, i) => (
                                                        <TableRow
                                                            key={row.lineNumber ?? i}
                                                            sx={{ bgcolor: row.isValid ? 'transparent' : 'action.hover' }}
                                                        >
                                                            <TableCell sx={{ fontSize: 12 }}>{row.lineNumber}</TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>{row.externalId ?? '—'}</TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>{row.companyName ?? '—'}</TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>{row.taxRegNumber ?? '—'}</TableCell>
                                                            <TableCell sx={{ fontSize: 12 }}>{row.email ?? '—'}</TableCell>
                                                            <TableCell sx={{ fontSize: 12, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                {row.isValid ? (
                                                                    <Typography variant="body2" color="success.main">{t('Valid') || 'OK'}</Typography>
                                                                ) : (
                                                                    <Typography variant="body2" color="error.main" title={row.errorMessage ?? ''}>{row.errorMessage ?? ''}</Typography>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}

                    {tabValue === 1 && (
                        <Box>
                            <div>
                                <h5>.csv ({t('ICountInvoices') || 'Invoices'})</h5>
                                <DropzoneArea
                                    key={invoicesFile ? invoicesFile.name : 'icount-invoices-empty'}
                                    onDrop={(files) => {
                                        if (files?.length) setInvoicesFile(files[0]);
                                        setInvoicesPreviewData(null);
                                    }}
                                    accept=".csv"
                                />
                                {invoicesFile && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {t('FileSelected')}: {invoicesFile.name}
                                    </Typography>
                                )}
                            </div>
                            <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
                                {sortCodeIdOptions?.length > 0 ? (
                                    <Autocomplete
                                        options={sortCodeIdOptions}
                                        getOptionLabel={(o) => `${o.sortCodeId} - ${o.sortCodeName ?? ''}`}
                                        value={sortCodeIdOptions.find((o) => String(o.sortCodeId) === String(defaultSortCodeId)) || null}
                                        onChange={(_, v) => setDefaultSortCodeId(v ? String(v.sortCodeId) : '100')}
                                        size="small"
                                        sx={{ minWidth: 200 }}
                                        renderInput={(params) => <TextField {...params} label={t('DefaultSortCode')} variant="standard" />}
                                    />
                                ) : (
                                    <TextField label={t('DefaultSortCode')} value={defaultSortCodeId} onChange={(e) => setDefaultSortCodeId(e.target.value)} size="small" sx={{ minWidth: 120 }} variant="standard" />
                                )}
                                <Autocomplete
                                    options={debitOptions}
                                    getOptionLabel={(o) => o.label || o.cardId || ''}
                                    value={debitOptions.find((o) => String(o.cardId) === String(defaultDebitAccountId)) || null}
                                    onChange={(_, v) => setDefaultDebitAccountId(v ? String(v.cardId) : '4')}
                                    size="small"
                                    sx={{ minWidth: 220 }}
                                    renderInput={(params) => <TextField {...params} label={t('DefaultCreditAccount') || 'Default credit account'} variant="standard" />}
                                />
                            </Box>
                            <Box display="flex" gap={1.5} mt={2}>
                                <Button variant="contained" onClick={runInvoicesPreview} disabled={!invoicesFile || invoicesProcessing}>
                                    {invoicesProcessing && !invoicesPreviewData ? t('Processing') : t('Preview')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleInvoicesImport}
                                    disabled={!invoicesPreviewData || invoicesProcessing || (invoicesPreviewData?.validCount ?? 0) === 0}
                                >
                                    {invoicesProcessing && invoicesPreviewData ? t('Processing') : t('Import')}
                                </Button>
                            </Box>
                            {invoicesPreviewData && invoicesPreviewData.entries?.length > 0 && (
                                <Box mt={3} sx={{ maxHeight: 600, overflow: 'auto' }}>
                                    {(() => {
                                        const entries = invoicesPreviewData.entries ?? [];
                                        const validEntries = entries.filter((e) => e.isValid);
                                        const sumAmount = validEntries.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
                                        const totalNet = validEntries.reduce((sum, e) => {
                                            const amt = Number(e.amount) || 0;
                                            const vatP = Number(e.vatPercent) || 0;
                                            return sum + (vatP > 0 ? amt / (1 + vatP / 100) : amt);
                                        }, 0);
                                        const totalVat = sumAmount - totalNet;
                                        const journalCount = validEntries.length;
                                        return (
                                            <>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {t('TotalLines') || 'Total lines'}: <strong>{invoicesPreviewData.totalLines}</strong>
                                                    {' | '}
                                                    {t('ValidCount') || 'Valid'}: <strong>{invoicesPreviewData.validCount}</strong>
                                                    {' | '}
                                                    {t('ErrorCount') || 'Errors'}: <strong>{invoicesPreviewData.errorCount}</strong>
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {t('NetAmount') || 'Total net'}: <strong>{formatAmount(totalNet)}</strong>
                                                    {' | '}
                                                    {t('VAT') || 'VAT'}: <strong>{formatAmount(totalVat)}</strong>
                                                    {' | '}
                                                    {t('TotalAmount') || 'Total amount'}: <strong>{formatAmount(sumAmount)}</strong>
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {t('ICountJournalEntriesToCreate') || 'Journal entries to create'}: <strong>{journalCount}</strong>
                                                </Typography>
                                                {invoicesPreviewData.errorMessages?.length > 0 && (
                                                    <Box mb={2} p={1.5} bgcolor="error.light" borderRadius={1} sx={{ maxHeight: 220, overflow: 'auto' }}>
                                                        <Typography variant="subtitle2" color="error.dark" sx={{ mb: 0.5 }}>
                                                            {t('Errors') || 'Errors'} ({invoicesPreviewData.errorMessages.length})
                                                        </Typography>
                                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                                            {invoicesPreviewData.errorMessages.map((msg, i) => (
                                                                <li key={i}>
                                                                    <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                        {msg}
                                                                    </Typography>
                                                                </li>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                )}
                                                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                                    <Table size="small" stickyHeader>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Line')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('DocumentDate')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Reference')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Client')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Description')}</TableCell>
                                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>{t('Amount')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>VAT%</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('CreditAccount') || 'Credit account'}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('ICountJournalType') || 'Type'}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Status')}</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {entries
                                                                .slice(invoicesPage * invoicesRowsPerPage, invoicesPage * invoicesRowsPerPage + invoicesRowsPerPage)
                                                                .map((row, i) => (
                                                                    <TableRow
                                                                        key={row.lineNumber ?? i}
                                                                        sx={{ bgcolor: row.isValid ? 'transparent' : 'action.hover' }}
                                                                    >
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.lineNumber}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.documentDate ?? '—'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.reference ?? '—'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{formatClientDisplay(row.customer, row.creditAccountName, row.creditAccount)}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12, maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }} title={row.description ?? ''}>
                                                                            {row.description ?? '—'}
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(row.amount)}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.vatPercent != null ? formatAmount(row.vatPercent) : '—'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.creditAccountName ?? row.creditAccount ?? '—'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{t('ICountIncome') || 'Income'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                            {row.isValid ? (
                                                                                <Typography variant="body2" color="success.main">{t('Valid') || 'OK'}</Typography>
                                                                            ) : (
                                                                                <Typography variant="body2" color="error.main" title={row.errorMessage ?? ''}>{row.errorMessage ?? ''}</Typography>
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                        </TableBody>
                                                    </Table>
                                                    <TablePagination
                                                        component="div"
                                                        count={entries.length}
                                                        page={invoicesPage}
                                                        onPageChange={(_, p) => setInvoicesPage(p)}
                                                        rowsPerPage={invoicesRowsPerPage}
                                                        onRowsPerPageChange={(e) => { setInvoicesRowsPerPage(parseInt(e.target.value, 10)); setInvoicesPage(0); }}
                                                        rowsPerPageOptions={[10, 25, 50]}
                                                        labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                                    />
                                                </TableContainer>
                                            </>
                                        );
                                    })()}
                                </Box>
                            )}
                        </Box>
                    )}

                    {tabValue === 2 && (
                        <Box>
                            <div>
                                <h5>.csv ({t('ICountInvrec') || 'Invrec'})</h5>
                                <DropzoneArea
                                    key={invrecFile ? invrecFile.name : 'icount-invrec-empty'}
                                    onDrop={(files) => {
                                        if (files?.length) setInvrecFile(files[0]);
                                        setInvrecPreviewData(null);
                                        setPaymentTypeToCardId({});
                                    }}
                                    accept=".csv"
                                />
                                {invrecFile && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {t('FileSelected')}: {invrecFile.name}
                                    </Typography>
                                )}
                            </div>
                            <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
                                {sortCodeIdOptions?.length > 0 ? (
                                    <>
                                        <Autocomplete
                                            options={sortCodeIdOptions}
                                            getOptionLabel={(o) => `${o.sortCodeId} - ${o.sortCodeName ?? ''}`}
                                            value={sortCodeIdOptions.find((o) => String(o.sortCodeId) === String(defaultSortCodeId)) || null}
                                            onChange={(_, v) => setDefaultSortCodeId(v ? String(v.sortCodeId) : '100')}
                                            size="small"
                                            sx={{ minWidth: 200 }}
                                            renderInput={(params) => <TextField {...params} label={t('DefaultSortCode')} variant="standard" />}
                                        />
                                        <Autocomplete
                                            options={sortCodeIdOptions}
                                            getOptionLabel={(o) => `${o.sortCodeId} - ${o.sortCodeName ?? ''}`}
                                            value={sortCodeIdOptions.find((o) => String(o.sortCodeId) === String(invrecPaymentSortCodeId)) || null}
                                            onChange={(_, v) => setInvrecPaymentSortCodeId(v ? String(v.sortCodeId) : '3')}
                                            size="small"
                                            sx={{ minWidth: 200 }}
                                            renderInput={(params) => <TextField {...params} label={t('PaymentSortCode')} variant="standard" />}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <TextField label={t('DefaultSortCode')} value={defaultSortCodeId} onChange={(e) => setDefaultSortCodeId(e.target.value)} size="small" sx={{ minWidth: 100 }} variant="standard" />
                                        <TextField label={t('PaymentSortCode')} value={invrecPaymentSortCodeId} onChange={(e) => setInvrecPaymentSortCodeId(e.target.value)} size="small" sx={{ minWidth: 100 }} variant="standard" />
                                    </>
                                )}
                                <Autocomplete
                                    options={debitOptions}
                                    getOptionLabel={(o) => o.label || o.cardId || ''}
                                    value={debitOptions.find((o) => String(o.cardId) === String(defaultDebitAccountId)) || null}
                                    onChange={(_, v) => setDefaultDebitAccountId(v ? String(v.cardId) : '4')}
                                    size="small"
                                    sx={{ minWidth: 220 }}
                                    renderInput={(params) => <TextField {...params} label={t('DefaultCreditAccount')} variant="standard" />}
                                />
                            </Box>
                            <Box display="flex" gap={1.5} mt={2}>
                                <Button variant="contained" onClick={runInvrecPreview} disabled={!invrecFile || invrecProcessing}>
                                    {invrecProcessing && !invrecPreviewData ? t('Processing') : t('Preview')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleInvrecImport}
                                    disabled={!invrecPreviewData || invrecProcessing || !allPaymentTypesMapped || (invrecPreviewData?.validCount ?? 0) === 0}
                                >
                                    {invrecProcessing && invrecPreviewData ? t('Processing') : t('Import')}
                                </Button>
                            </Box>
                            {invrecPreviewData && distinctPaymentTypes.length > 0 && (
                                <Box mt={2} p={1.5} bgcolor="grey.50" borderRadius={1}>
                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                                        {t('PaymentTypeMapping') || 'Payment type → Debit account'}
                                    </Typography>
                                    <Box display="flex" flexWrap="wrap" gap={2}>
                                        {distinctPaymentTypes.map((pt) => (
                                            <Autocomplete
                                                key={pt}
                                                options={debitOptions}
                                                getOptionLabel={(o) => o.label || o.cardId || ''}
                                                value={debitOptions.find((o) => String(o.cardId) === String(paymentTypeToCardId[pt] ?? '')) || null}
                                                onChange={(_, v) => setPaymentTypeToCardId((prev) => ({ ...prev, [pt]: v ? String(v.cardId) : '' }))}
                                                size="small"
                                                sx={{ minWidth: 220 }}
                                                renderInput={(params) => <TextField {...params} label={pt} variant="outlined" />}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            {invrecPreviewData && invrecEntries.length > 0 && (
                                <Box mt={3} sx={{ maxHeight: 600, overflow: 'auto' }}>
                                    {(() => {
                                        const validEntries = invrecEntries.filter((e) => e.isValid);
                                        const entriesWithMappedPayment = validEntries.filter((e) => (e.paymentType ?? '').trim() && paymentTypeToCardId[(e.paymentType ?? '').trim()]);
                                        const sumIncomeAmount = validEntries.reduce((sum, e) => {
                                            const hasMappedPayment = (e.paymentType ?? '').trim() && paymentTypeToCardId[(e.paymentType ?? '').trim()];
                                            const incomeAmt = hasMappedPayment ? (Number(e.customerPaymentAmount) || Number(e.amount) || 0) : (Number(e.amount) || 0);
                                            return sum + incomeAmt;
                                        }, 0);
                                        const sumPaymentAmount = entriesWithMappedPayment.reduce(
                                            (sum, e) => sum + (Number(e.customerPaymentAmount) || Number(e.amount) || 0),
                                            0
                                        );
                                        const totalNet = validEntries.reduce((sum, e) => {
                                            const amt = (Number(e.customerPaymentAmount) || Number(e.amount) || 0);
                                            const vatP = Number(e.vatPercent) || 0;
                                            return sum + (vatP > 0 ? amt / (1 + vatP / 100) : amt);
                                        }, 0);
                                        const totalVat = sumIncomeAmount - totalNet;
                                        const incomeJournalCount = validEntries.length;
                                        const paymentJournalCount = entriesWithMappedPayment.length;
                                        const totalJournalCount = incomeJournalCount + paymentJournalCount;
                                        return (
                                            <>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {t('TotalLines') || 'Total lines'}: <strong>{invrecPreviewData.totalRows}</strong>
                                                    {' | '}
                                                    {t('ValidCount') || 'Valid'}: <strong>{invrecPreviewData.validCount}</strong>
                                                    {' | '}
                                                    {t('ErrorCount') || 'Errors'}: <strong>{invrecPreviewData.errorCount}</strong>
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {t('NetAmount') || 'Total net'}: <strong>{formatAmount(totalNet)}</strong>
                                                    {' | '}
                                                    {t('VAT') || 'VAT'}: <strong>{formatAmount(totalVat)}</strong>
                                                    {' | '}
                                                    {t('TotalAmount') || 'Total amount'}: <strong>{formatAmount(sumIncomeAmount)}</strong>
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {t('SumAmount') || 'Sum Amount'} ({t('ICountIncome') || 'Income'}): <strong>{formatAmount(sumIncomeAmount)}</strong>
                                                    {paymentJournalCount > 0 && (
                                                        <>
                                                            {' | '}
                                                            {t('SumAmount')} ({t('ICountPayment') || 'Payment'}): <strong>{formatAmount(sumPaymentAmount)}</strong>
                                                        </>
                                                    )}
                                                    {' | '}
                                                    {t('ICountJournalEntriesToCreate') || 'Journal entries to create'}: <strong>{totalJournalCount}</strong>
                                                    {' '}({t('ICountIncome')}: {incomeJournalCount}
                                                    {paymentJournalCount > 0 && `, ${t('ICountPayment')}: ${paymentJournalCount}`})
                                                </Typography>
                                                {invrecPreviewData.errorMessages?.length > 0 && (
                                                    <Box mb={2} p={1.5} bgcolor="error.light" borderRadius={1} sx={{ maxHeight: 220, overflow: 'auto' }}>
                                                        <Typography variant="subtitle2" color="error.dark" sx={{ mb: 0.5 }}>
                                                            {t('Errors') || 'Errors'} ({invrecPreviewData.errorMessages.length})
                                                        </Typography>
                                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                                            {invrecPreviewData.errorMessages.map((msg, i) => (
                                                                <li key={i}>
                                                                    <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                        {msg}
                                                                    </Typography>
                                                                </li>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                )}
                                                {(() => {
                                                    const journalRows = [];
                                                    invrecEntries.forEach((row, idx) => {
                                                        const hasMappedPayment = (row.paymentType ?? '').trim() && paymentTypeToCardId[(row.paymentType ?? '').trim()];
                                                        const incomeAmount = hasMappedPayment ? (Number(row.customerPaymentAmount) || Number(row.amount) || 0) : (Number(row.amount) || 0);
                                                        const paymentAmount = Number(row.customerPaymentAmount) || Number(row.amount) || 0;
                                                        journalRows.push({
                                                            ...row,
                                                            journalType: 'Income',
                                                            amount: incomeAmount,
                                                            journalKey: `${row.lineNumber}-income-${idx}`,
                                                        });
                                                        if (hasMappedPayment) {
                                                            journalRows.push({
                                                                ...row,
                                                                journalType: 'Payment',
                                                                amount: paymentAmount,
                                                                journalKey: `${row.lineNumber}-payment-${idx}`,
                                                            });
                                                        }
                                                    });
                                                    const paginatedJournalRows = journalRows.slice(
                                                        invrecPage * invrecRowsPerPage,
                                                        invrecPage * invrecRowsPerPage + invrecRowsPerPage
                                                    );
                                                    return (
                                                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                                    <Table size="small" stickyHeader>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Line')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('ICountJournalType') || 'Type'}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('DocumentDate')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Reference')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Client')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('PaymentType')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Description')}</TableCell>
                                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>{t('Amount')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Status')}</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {paginatedJournalRows.map((jRow) => (
                                                                <TableRow
                                                                    key={jRow.journalKey}
                                                                    sx={{ bgcolor: jRow.isValid ? 'transparent' : 'action.hover' }}
                                                                >
                                                                    <TableCell sx={{ fontSize: 12 }}>{jRow.lineNumber}</TableCell>
                                                                    <TableCell sx={{ fontSize: 12 }}>
                                                                        <Typography variant="body2" fontWeight={jRow.journalType === 'Payment' ? 600 : 400} color={jRow.journalType === 'Payment' ? 'primary.main' : 'text.primary'}>
                                                                            {jRow.journalType === 'Income' ? (t('ICountIncome') || 'Income') : (t('ICountPayment') || 'Payment')}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell sx={{ fontSize: 12 }}>{jRow.documentDate ?? '—'}</TableCell>
                                                                    <TableCell sx={{ fontSize: 12 }}>{jRow.reference ?? '—'}</TableCell>
                                                                    <TableCell sx={{ fontSize: 12 }}>{formatClientDisplay(jRow.customer, jRow.creditAccountName, jRow.creditAccount)}</TableCell>
                                                                    <TableCell sx={{ fontSize: 12 }}>{jRow.paymentType ?? '—'}</TableCell>
                                                                    <TableCell sx={{ fontSize: 12, maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }} title={jRow.description ?? ''}>
                                                                        {jRow.description ?? '—'}
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(jRow.amount)}</TableCell>
                                                                    <TableCell sx={{ fontSize: 12, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                        {jRow.isValid ? (
                                                                            <Typography variant="body2" color="success.main">{t('Valid') || 'OK'}</Typography>
                                                                        ) : (
                                                                            <Typography variant="body2" color="error.main" title={jRow.errorMessage ?? ''}>{jRow.errorMessage ?? ''}</Typography>
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                    <TablePagination
                                                        component="div"
                                                        count={journalRows.length}
                                                        page={invrecPage}
                                                        onPageChange={(_, p) => setInvrecPage(p)}
                                                        rowsPerPage={invrecRowsPerPage}
                                                        onRowsPerPageChange={(e) => { setInvrecRowsPerPage(parseInt(e.target.value, 10)); setInvrecPage(0); }}
                                                        rowsPerPageOptions={[10, 25, 50]}
                                                        labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                                    />
                                                </TableContainer>
                                                    );
                                                })()}
                                            </>
                                        );
                                    })()}
                                </Box>
                            )}
                        </Box>
                    )}

                    {tabValue === 3 && (
                        <Box>
                            <div>
                                <h5>.csv ({t('ICountRefunds') || 'Refunds'})</h5>
                                <DropzoneArea
                                    key={refundsFile ? refundsFile.name : 'icount-refunds-empty'}
                                    onDrop={(files) => {
                                        if (files?.length) setRefundsFile(files[0]);
                                        setRefundsPreviewData(null);
                                    }}
                                    accept=".csv"
                                />
                                {refundsFile && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        {t('FileSelected')}: {refundsFile.name}
                                    </Typography>
                                )}
                            </div>
                            <Box display="flex" flexWrap="wrap" gap={2} mt={2}>
                                {sortCodeIdOptions?.length > 0 ? (
                                    <Autocomplete
                                        options={sortCodeIdOptions}
                                        getOptionLabel={(o) => `${o.sortCodeId} - ${o.sortCodeName ?? ''}`}
                                        value={sortCodeIdOptions.find((o) => String(o.sortCodeId) === String(defaultSortCodeId)) || null}
                                        onChange={(_, v) => setDefaultSortCodeId(v ? String(v.sortCodeId) : '100')}
                                        size="small"
                                        sx={{ minWidth: 200 }}
                                        renderInput={(params) => <TextField {...params} label={t('DefaultSortCode')} variant="standard" />}
                                    />
                                ) : (
                                    <TextField label={t('DefaultSortCode')} value={defaultSortCodeId} onChange={(e) => setDefaultSortCodeId(e.target.value)} size="small" sx={{ minWidth: 120 }} variant="standard" />
                                )}
                                <Autocomplete
                                    options={debitOptions}
                                    getOptionLabel={(o) => o.label || o.cardId || ''}
                                    value={debitOptions.find((o) => String(o.cardId) === String(defaultDebitAccountId)) || null}
                                    onChange={(_, v) => setDefaultDebitAccountId(v ? String(v.cardId) : '4')}
                                    size="small"
                                    sx={{ minWidth: 220 }}
                                    renderInput={(params) => <TextField {...params} label={t('DefaultCreditAccount')} variant="standard" />}
                                />
                            </Box>
                            <Box display="flex" gap={1.5} mt={2}>
                                <Button variant="contained" onClick={runRefundsPreview} disabled={!refundsFile || refundsProcessing}>
                                    {refundsProcessing && !refundsPreviewData ? t('Processing') : t('Preview')}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={handleRefundsImport}
                                    disabled={!refundsPreviewData || refundsProcessing || (refundsPreviewData?.validCount ?? 0) === 0}
                                >
                                    {refundsProcessing && refundsPreviewData ? t('Processing') : t('Import')}
                                </Button>
                            </Box>
                            {refundsPreviewData && refundsPreviewData.entries?.length > 0 && (
                                <Box mt={3} sx={{ maxHeight: 600, overflow: 'auto' }}>
                                    {(() => {
                                        const entries = refundsPreviewData.entries ?? [];
                                        const validEntries = entries.filter((e) => e.isValid);
                                        const sumAmount = validEntries.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
                                        const totalNet = validEntries.reduce((sum, e) => {
                                            const amt = Number(e.amount) || 0;
                                            const vatP = Number(e.vatPercent) || 0;
                                            return sum + (vatP > 0 ? amt / (1 + vatP / 100) : amt);
                                        }, 0);
                                        const totalVat = sumAmount - totalNet;
                                        const journalCount = validEntries.length;
                                        return (
                                            <>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {t('TotalLines') || 'Total lines'}: <strong>{refundsPreviewData.totalLines}</strong>
                                                    {' | '}
                                                    {t('ValidCount') || 'Valid'}: <strong>{refundsPreviewData.validCount}</strong>
                                                    {' | '}
                                                    {t('ErrorCount') || 'Errors'}: <strong>{refundsPreviewData.errorCount}</strong>
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {t('NetAmount') || 'Total net'}: <strong>{formatAmount(totalNet)}</strong>
                                                    {' | '}
                                                    {t('VAT') || 'VAT'}: <strong>{formatAmount(totalVat)}</strong>
                                                    {' | '}
                                                    {t('TotalAmount') || 'Total amount'}: <strong>{formatAmount(sumAmount)}</strong>
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {t('ICountJournalEntriesToCreate') || 'Journal entries to create'}: <strong>{journalCount}</strong>
                                                </Typography>
                                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                                                    {t('AmountsShownAsNegative') || 'Amounts stored as negative'}
                                                </Typography>
                                                {refundsPreviewData.errorMessages?.length > 0 && (
                                                    <Box mb={2} p={1.5} bgcolor="error.light" borderRadius={1} sx={{ maxHeight: 220, overflow: 'auto' }}>
                                                        <Typography variant="subtitle2" color="error.dark" sx={{ mb: 0.5 }}>
                                                            {t('Errors') || 'Errors'} ({refundsPreviewData.errorMessages.length})
                                                        </Typography>
                                                        <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                                            {refundsPreviewData.errorMessages.map((msg, i) => (
                                                                <li key={i}>
                                                                    <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                                        {msg}
                                                                    </Typography>
                                                                </li>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                )}
                                                <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                                                    <Table size="small" stickyHeader>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Line')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('DocumentDate')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Reference')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Client')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Description')}</TableCell>
                                                                <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>{t('Amount')}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>VAT%</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('CreditAccount') || 'Credit account'}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('ICountJournalType') || 'Type'}</TableCell>
                                                                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{t('Status')}</TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {entries
                                                                .slice(refundsPage * refundsRowsPerPage, refundsPage * refundsRowsPerPage + refundsRowsPerPage)
                                                                .map((row, i) => (
                                                                    <TableRow
                                                                        key={row.lineNumber ?? i}
                                                                        sx={{ bgcolor: row.isValid ? 'transparent' : 'action.hover' }}
                                                                    >
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.lineNumber}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.documentDate ?? '—'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.reference ?? '—'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{formatClientDisplay(row.customer, row.creditAccountName, row.creditAccount)}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12, maxWidth: 200, whiteSpace: 'normal', wordBreak: 'break-word' }} title={row.description ?? ''}>
                                                                            {row.description ?? '—'}
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ fontSize: 12 }}>{formatAmount(row.amount)}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.vatPercent != null ? formatAmount(row.vatPercent) : '—'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{row.creditAccountName ?? row.creditAccount ?? '—'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12 }}>{t('Refund') || 'Refund'}</TableCell>
                                                                        <TableCell sx={{ fontSize: 12, maxWidth: 320, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                            {row.isValid ? (
                                                                                <Typography variant="body2" color="success.main">{t('Valid') || 'OK'}</Typography>
                                                                            ) : (
                                                                                <Typography variant="body2" color="error.main" title={row.errorMessage ?? ''}>{row.errorMessage ?? ''}</Typography>
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                        </TableBody>
                                                    </Table>
                                                    <TablePagination
                                                        component="div"
                                                        count={entries.length}
                                                        page={refundsPage}
                                                        onPageChange={(_, p) => setRefundsPage(p)}
                                                        rowsPerPage={refundsRowsPerPage}
                                                        onRowsPerPageChange={(e) => { setRefundsRowsPerPage(parseInt(e.target.value, 10)); setRefundsPage(0); }}
                                                        rowsPerPageOptions={[10, 25, 50]}
                                                        labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                                    />
                                                </TableContainer>
                                            </>
                                        );
                                    })()}
                                </Box>
                            )}
                        </Box>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ICountImportModal;
